import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";

import { API_BASE_URL, TOKEN_STORAGE_KEY } from "./constants";
import { currentCorrelationId } from "./correlation";
import type { AuthTokens } from "@/types/auth";
import type { ApiEnvelope } from "@/types/api";

// ---------------------------------------------------------------- Storage
export function loadTokens(): AuthTokens | null {
  const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
  return raw ? (JSON.parse(raw) as AuthTokens) : null;
}
export function saveTokens(t: AuthTokens) {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(t));
}
export function clearTokens() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

// ---------------------------------------------------------------- Instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------- Interceptors

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const tokens = loadTokens();
  if (tokens?.access) {
    config.headers.set("Authorization", `Bearer ${tokens.access}`);
  }
  config.headers.set("X-Correlation-Id", currentCorrelationId());
  return config;
});

// Refresh lock so concurrent requests share ONE refresh call
let refreshPromise: Promise<AuthTokens> | null = null;

async function refreshAccessToken(): Promise<AuthTokens> {
  const tokens = loadTokens();
  if (!tokens?.refresh) throw new Error("No refresh token");

  const res = await axios.post<{ access: string; refresh?: string }>(
    `${API_BASE_URL}/auth/token/refresh`,
    { refresh: tokens.refresh },
  );
  const next: AuthTokens = {
    access: res.data.access,
    refresh: res.data.refresh ?? tokens.refresh,
  };
  saveTokens(next);
  return next;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Attempt token refresh on 401 (except the refresh endpoint itself)
    if (
      error.response?.status === 401 &&
      !original?._retry &&
      !original.url?.includes("/auth/token/refresh") &&
      !original.url?.includes("/auth/login")
    ) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise ?? refreshAccessToken();
        const next = await refreshPromise;
        if (original.headers) {
          original.headers.set("Authorization", `Bearer ${next.access}`);
        }
        return api(original);
      } catch {
        // FIX: Properly clear the Zustand store state to break the infinite loop!
        clearTokens();
        import("@/store/auth-store").then((module) => {
          module.useAuthStore.getState().logout();
          window.location.href = "/login";
        });
        return Promise.reject(error);
      } finally {
        refreshPromise = null;
      }
    }

    // User-facing toast for known API errors
    const payload = error.response?.data;
    if (payload && "error" in payload && payload.error) {
      const { code, message } = payload.error;
      // Silence 401 noise when unauthenticated pages redirect
      if (error.response?.status !== 401) {
        toast.error(message || code || "Request failed");
      }
    } else if (error.code === "ECONNABORTED") {
      toast.error("Request timed out. Please try again.");
    } else if (!error.response) {
      toast.error("Network error. Check your connection.");
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------- Helpers

export async function get<T>(url: string, params?: Record<string, unknown>) {
  const r = await api.get<ApiEnvelope<T>>(url, { params });
  if (!r.data.success) throw new Error(r.data.error.message);
  return r.data.data;
}

export async function getPaginated<T>(
  url: string,
  params?: Record<string, unknown>,
) {
  const r = await api.get<ApiEnvelope<T[]>>(url, { params });
  if (!r.data.success) throw new Error(r.data.error.message);
  
  // Safely extract the results Array regardless of pagination wrapper presence
  let results: T[] = [];
  if (Array.isArray(r.data.data)) {
      results = r.data.data;
  } else if (r.data.data && typeof r.data.data === 'object' && 'results' in r.data.data) {
      results = (r.data.data as any).results as T[];
  }

  return {
    results: results,
    ...(r.data.pagination ?? {
      count: results.length,
      num_pages: 1,
      page: 1,
      page_size: results.length,
      next: null,
      previous: null,
    }),
  };
}

export async function post<T>(
  url: string,
  body?: unknown,
  opts?: { idempotencyKey?: string; headers?: Record<string, string> },
) {
  const headers: Record<string, string> = { ...(opts?.headers ?? {}) };
  if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  // 🛠️ Passing headers to axios post
  const r = await api.post<ApiEnvelope<T>>(url, body, { headers });
  if (!r.data.success) throw new Error(r.data.error.message);
  return r.data.data;
}

export async function patch<T>(
  url: string, 
  body?: unknown, 
  opts?: { headers?: Record<string, string> }
) {
  const headers = opts?.headers || {};
  // 🛠️ FIXED: We must pass the headers object to axios patch so it sends multipart/form-data!
  const r = await api.patch<ApiEnvelope<T>>(url, body, { headers });
  if (!r.data.success) throw new Error(r.data.error.message);
  return r.data.data;
}

// 🛠️ NEW: Added delete method so we don't have to call `api.delete` manually in extensions
export async function del<T>(url: string) {
  const r = await api.delete<ApiEnvelope<T>>(url);
  if (!r.data.success) throw new Error(r.data.error.message);
  return r.data.data;
}